import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AuditFormat } from './audit-format';
import { AuditDocumentAccess } from './audit.models';

/** Detail panel of a document access entry: who, when, how (channel, version), how many times; same document / user */
@Component({
  selector: 'iu-audit-document-detail',
  standalone: true,
  imports: [TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @let a = access();
    <header class="aud-head">
      @if (a) {
        <span class="aud-badge aud-badge--doc-{{ a.action }}">{{ 'audit.docAction.' + a.action | translate }}</span>
        <h2 class="aud-title" [title]="a.fileName || a.docId">
          <i class="fa-solid fa-file-lines" aria-hidden="true"></i>{{ a.fileName || a.docId }}
        </h2>
      }
      <button type="button" class="aud-icon-btn aud-close" (click)="closed.emit()" [attr.aria-label]="'audit.actions.close' | translate">
        <i class="fa-solid fa-xmark" aria-hidden="true"></i>
      </button>
    </header>
    @if (a) {
      <div class="aud-body">
        <dl class="aud-meta">
          <div>
            <dt>{{ 'audit.document.last' | translate }}</dt>
            <dd [title]="fmt().exact(a.last)">{{ fmt().dateTime(a.last) }}</dd>
          </div>
          @if (a.count > 1 || a.first !== a.last) {
            <div>
              <dt>{{ 'audit.document.first' | translate }}</dt>
              <dd [title]="fmt().exact(a.first)">{{ fmt().dateTime(a.first) }}</dd>
            </div>
          }
          <div>
            <dt>{{ 'audit.document.times' | translate }}</dt>
            <dd>{{ fmt().number(a.count) }}</dd>
          </div>
          <div>
            <dt>{{ 'audit.change.user' | translate }}</dt>
            <dd>
              {{ a.userName || ('audit.change.unknownUser' | translate: { id: a.userId }) }}
              @if (a.roleName) {
                <small class="aud-muted">{{ a.roleName }}</small>
              }
            </dd>
          </div>
          <div>
            <dt>{{ 'audit.document.channel' | translate }}</dt>
            <dd>{{ 'audit.docChannelLong.' + (a.channel || 'app') | translate }}</dd>
          </div>
          <div>
            <dt>{{ 'audit.document.version' | translate }}</dt>
            <dd>{{ a.version > 0 ? 'v' + a.version : ('audit.document.current' | translate) }}</dd>
          </div>
          <div class="aud-meta-wide">
            <dt>{{ 'audit.document.id' | translate }}</dt>
            <dd><code class="aud-wrap">{{ a.docId }}</code></dd>
          </div>
        </dl>
        @if (a.channel === 'link') {
          <p class="aud-note">{{ 'audit.document.linkNote' | translate }}</p>
        }
        <div class="aud-links">
          <button type="button" class="aud-link-btn" (click)="filterDocument.emit(a)">
            <i class="fa-solid fa-file-lines" aria-hidden="true"></i>{{ 'audit.actions.documentReads' | translate }}
          </button>
          @if (a.userId > 0) {
            <button type="button" class="aud-link-btn" (click)="filterUser.emit(a)">
              <i class="fa-solid fa-user" aria-hidden="true"></i>{{ 'audit.change.sameUser' | translate }}
            </button>
          }
        </div>
      </div>
    }
  `,
  styleUrl: './audit-detail.scss',
})
export class AuditDocumentDetailComponent {
  readonly access = input<AuditDocumentAccess | null>(null);
  readonly fmt = input.required<AuditFormat>();

  readonly closed = output<void>();
  readonly filterDocument = output<AuditDocumentAccess>();
  readonly filterUser = output<AuditDocumentAccess>();
}
